/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { FormGroup, FormItem } from '../../types/CustomForm'
import {
  filterEnabledGroups,
  sortGroupsByIndex,
  sortItemsByIndex,
  mapFormItemTypeToKind,
  extractOptions,
} from '../../utils/customFormHelpers'
import {
  Container,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  QuestionsGrid,
  QuestionContainer,
} from './styles'
import RichTextRenderer from '../RichTextRenderer'
import { FieldValue } from './index'

interface DynamicFormReadOnlyProps {
  groups: FormGroup[]
  answers: Record<string, FieldValue>
}

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string
  value?: string | null
}) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      gap: 2px;
    `}
  >
    <Text variant='paragraphXSmall' weight='medium' color={colors.NEUTRAL_50}>
      {label}
    </Text>
    <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
      {value || '-'}
    </Text>
  </div>
)

const CheckboxReadOnly = ({
  label,
  checked,
}: {
  label: string
  checked: boolean
}) => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      gap: 2px;
    `}
  >
    <Text variant='paragraphXSmall' weight='medium' color={colors.NEUTRAL_50}>
      {label}
    </Text>
    <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
      {checked ? 'Yes' : 'No'}
    </Text>
  </div>
)

const ContentSection = ({ content }: { content: string }) => (
  <div css={SectionContainer}>
    <div
      css={css`
        height: 1px;
        background: ${colors.VERY_DARK_GRAYISH_BLUE};
        margin: 0;
      `}
    />
    <RichTextRenderer content={content} />
    <div
      css={css`
        height: 1px;
        background: ${colors.VERY_DARK_GRAYISH_BLUE};
        margin: 0;
      `}
    />
  </div>
)

/**
 * Resolves the display value for a form item based on its type and the raw answer.
 */
const resolveDisplayValue = (
  item: FormItem,
  rawValue: FieldValue
): string | null => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null
  }

  const kind = mapFormItemTypeToKind(item.form_item_type.id)

  switch (kind) {
    case 'relationship':
    case 'teamOfficialRole':
    case 'dropdown': {
      const options = extractOptions(item)
      const match = options.find((opt) => opt.value === String(rawValue))
      return match?.label || String(rawValue)
    }
    case 'checkbox': {
      // Handled by CheckboxReadOnly
      return null
    }
    default:
      return String(rawValue)
  }
}

/**
 * DynamicFormReadOnly renders form groups and fields as read-only label/value pairs.
 * Used on the standalone registration details page instead of disabled form inputs.
 */
const DynamicFormReadOnly = ({ groups, answers }: DynamicFormReadOnlyProps) => {
  const enabledGroups = filterEnabledGroups(groups)
  const sortedGroups = sortGroupsByIndex(enabledGroups).map(sortItemsByIndex)

  if (sortedGroups.length === 0) {
    return null
  }

  return (
    <div css={Container}>
      {sortedGroups.map((group) => {
        const isContentGroup = (group as any).is_content
        const content = (group as any).content

        if (isContentGroup && content) {
          return <ContentSection key={group.id} content={content} />
        }

        return (
          <div key={group.id} css={SectionContainer}>
            <div css={SectionHeader}>
              <div css={SectionTitle}>{group.name}</div>
              {group.description && (
                <div css={SectionSubtitle}>{group.description}</div>
              )}
            </div>

            <div css={QuestionsGrid}>
              {group.items.map((item) => {
                const kind = mapFormItemTypeToKind(item.form_item_type.id)
                const isWideField = ['textarea', 'checkbox'].includes(kind)
                const rawValue = answers[item.id]
                const label = item.display_label || item.name

                if (kind === 'checkbox') {
                  const checked =
                    rawValue === true || rawValue === 1 || rawValue === '1'
                  return (
                    <div
                      key={item.id}
                      css={[
                        QuestionContainer,
                        isWideField && { gridColumn: '1 / -1' },
                      ]}
                    >
                      <CheckboxReadOnly label={label} checked={checked} />
                    </div>
                  )
                }

                const displayValue = resolveDisplayValue(item, rawValue)

                return (
                  <div
                    key={item.id}
                    css={[
                      QuestionContainer,
                      isWideField && { gridColumn: '1 / -1' },
                    ]}
                  >
                    <ReadOnlyField label={label} value={displayValue} />
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default DynamicFormReadOnly
